import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BadgeCheck,
  BarChart3,
  FileText,
  FolderTree,
  Image,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  RotateCcw,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Star,
  Tag,
  Truck,
  Users,
  Warehouse,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import { useAdminLogout } from '../hooks/useAdminAuth';

const NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/admin/shipments', label: 'Shipments', icon: Truck },
  { to: '/admin/refunds', label: 'Refunds', icon: RotateCcw },
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/claims', label: 'Claims', icon: BadgeCheck },
  { to: '/admin/inventory', label: 'Inventory', icon: Warehouse },
  { to: '/admin/customers', label: 'Customers', icon: Users },
  { to: '/admin/reviews', label: 'Reviews', icon: Star },
  { to: '/admin/coupons', label: 'Coupons', icon: Tag },
  { to: '/admin/collections', label: 'Collections', icon: FolderTree },
  { to: '/admin/content', label: 'Content', icon: FileText },
  { to: '/admin/media', label: 'Media', icon: Image },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
  { to: '/admin/admin-users', label: 'Admin Users', icon: ShieldCheck },
  { to: '/admin/activity', label: 'Activity', icon: Activity },
];

export function AdminSidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const admin = useAdminAuthStore((s) => s.admin);
  const setAdmin = useAdminAuthStore((s) => s.setAdmin);
  const logout = useAdminLogout();

  function isActive(to: string) {
    if (to === '/admin/dashboard') return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    return location.pathname.startsWith(to);
  }

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => toast.success('Signed out'),
      onSettled: () => {
        setAdmin(null);
        navigate('/admin/login');
      },
    });
  }

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 inline-flex items-center justify-center h-9 w-9 rounded bg-forest text-ivory shadow-soft"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-charcoal/50 md:hidden" onClick={() => setOpen(false)} />}

      <aside
        className={cn(
          'z-50 flex h-screen w-64 shrink-0 flex-col bg-forest text-ivory transition-transform duration-200 md:sticky md:top-0 md:translate-x-0',
          'fixed inset-y-0 left-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-ivory/10">
          <span className="font-heading text-2xl tracking-wide text-ivory">ORENYVA</span>
          <button onClick={() => setOpen(false)} className="md:hidden text-ivory/70 hover:text-ivory" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={cn(
                  'flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors',
                  active ? 'bg-ivory/10 text-ivory font-semibold' : 'text-ivory/70 hover:bg-ivory/5 hover:text-ivory',
                )}
              >
                <Icon size={16} className={active ? 'text-bamboo' : 'text-ivory/50'} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-ivory/10 px-4 py-4">
          <div className="mb-3">
            <p className="text-sm font-semibold text-ivory truncate">
              {admin ? `${admin.firstName} ${admin.lastName}` : 'Admin'}
            </p>
            <p className="text-xs text-ivory/50 truncate">{admin?.email}</p>
            {admin?.role === 'staff' && (
              <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-bamboo/90">Staff access</span>
            )}
          </div>
          <button
            onClick={handleLogout}
            disabled={logout.isPending}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-ivory/70 hover:bg-ivory/5 hover:text-ivory transition-colors disabled:opacity-50"
          >
            <LogOut size={16} />
            {logout.isPending ? 'Signing out…' : 'Logout'}
          </button>
        </div>
      </aside>
    </>
  );
}
